import React, { useState } from "react";
import InboxView from "../../components/InboxView";
import { circlesApi } from "./circlesApi";
import { Modal, StatusBadge, inr, phoneText, shortDate, useBusy, useCirclesList } from "./circlesShared";
import "../../assets/css/PageHeading.css";

const TABS = [
  { id: "Registered", label: "To check", tone: "#2563eb" },
  { id: "Confirmed", label: "Confirmed", tone: "#16a34a" },
  { id: "Rejected", label: "Rejected", tone: "#64748b" },
  { id: "", label: "All", tone: "#1e3a8a" },
];

function ConfirmModal({ item, onClose, onDone }) {
  const [commission, setCommission] = useState(String(item.Default_Commission ?? 0));
  const [giftApplied, setGiftApplied] = useState(false);
  const [note, setNote] = useState("");
  const { busy, error, run } = useBusy();
  const submit = () =>
    run(async () => {
      await circlesApi.confirmReferral(item.App_Referral_Id, { commission: Number(commission), gift_applied: giftApplied, note });
      onDone();
    });
  return (
    <Modal
      title={`Confirm ${item.Ref}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--primary" disabled={busy}>
            Confirm registration
          </button>
        </>
      }
    >
      <p className="wl-muted">
        {item.Customer_Name} · {phoneText(item.Mobile)} · {item.App_Name} ({item.Platform === "ios" ? "iPhone" : "Android"})
        <br />
        Code {item.Agent_Code} · {item.Agent_Name}
      </p>
      <p className="cx-hint">
        Confirm only after checking in the {item.App_Name} admin that this number downloaded the app and registered with code {item.Agent_Code}.
      </p>
      <label className="wl-field">
        <span>Agent's commission (₹, 0 for none)</span>
        <input type="number" min="0" step="1" value={commission} onChange={(e) => setCommission(e.target.value)} />
      </label>
      <label className="cx-check">
        <input type="checkbox" checked={giftApplied} onChange={(e) => setGiftApplied(e.target.checked)} />
        <span>The customer's gift ({item.Gift_Label}) is already applied in the app</span>
      </label>
      <label className="wl-field">
        <span>Note (optional)</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} />
      </label>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

function RejectModal({ item, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const { busy, error, run } = useBusy();
  const submit = () =>
    run(async () => {
      await circlesApi.rejectReferral(item.App_Referral_Id, reason);
      onDone();
    });
  return (
    <Modal
      title={`Reject ${item.Ref}`}
      onClose={onClose}
      busy={busy}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="wl-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="wl-btn wl-btn--danger" disabled={busy}>
            Reject
          </button>
        </>
      }
    >
      <label className="wl-field">
        <span>Reason (the agent sees it)</span>
        <select value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Choose a reason…</option>
          <option>Not registered in the app</option>
          <option>Existing customer</option>
          <option>Referral code doesn't match</option>
          <option>Duplicate or test registration</option>
        </select>
      </label>
      {error ? <div className="wl-alert wl-alert--error">{error}</div> : null}
    </Modal>
  );
}

export default function CirclesAppRegistrations() {
  const [tab, setTab] = useState("Registered");
  const [confirming, setConfirming] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [notice, setNotice] = useState(null);
  const { busy, run } = useBusy();
  const { rows, extra, loading, error, reload } = useCirclesList(() => circlesApi.appReferrals({ status: tab || undefined }), [tab]);

  const markGift = (r) =>
    run(async () => {
      await circlesApi.giftApplied(r.App_Referral_Id);
      setNotice({ type: "success", text: `Gift marked as applied for ${r.Customer_Name}.` });
      reload();
    });

  const giftText = (r) => (r.Gift_Status === "Applied" ? `Applied ${shortDate(r.Gift_Applied_On)}` : r.Gift_Status === "Pending" ? "To apply" : "—");

  const actions = (r) =>
    r.Status === "Registered" ? (
      <div className="cx-actions">
        <button type="button" className="wl-btn wl-btn--primary wl-btn--sm" onClick={() => setConfirming(r)}>
          Confirm
        </button>
        <button type="button" className="wl-btn wl-btn--sm" onClick={() => setRejecting(r)}>
          Reject
        </button>
      </div>
    ) : r.Status === "Confirmed" && r.Gift_Status !== "Applied" ? (
      <button type="button" className="wl-btn wl-btn--sm" disabled={busy} onClick={() => markGift(r)}>
        Gift applied
      </button>
    ) : (
      <span className="wl-sub">{r.Note || "—"}</span>
    );

  return (
    <div className="p-2 p-md-3">
      <div className="co-page-head">
        <h2 className="co-page-head__title">App Registrations</h2>
      </div>
      <p className="cx-hint mb-2">
        Customers who downloaded a client app and registered with an agent's referral code. Nothing is given until you confirm: then apply the customer's gift in the app
        and the agent's commission is paid with the next payout.
      </p>
      <InboxView
        tabs={TABS}
        counts={extra.counts || {}}
        tab={tab}
        onTab={setTab}
        onRefresh={reload}
        notice={notice}
        onDismissNotice={() => setNotice(null)}
        error={error}
        loading={loading}
        rows={rows}
        rowKey={(r) => r.App_Referral_Id}
        empty="No registrations here."
        columns={[
          { key: "ref", label: "Registration", render: (r) => (<><div className="wl-name">{r.Ref}</div><div className="wl-sub">{shortDate(r.Registered_On)}</div></>) },
          { key: "customer", label: "Customer", render: (r) => (<><div className="wl-name">{r.Customer_Name}</div><div className="wl-sub">{phoneText(r.Mobile)} · {r.Platform === "ios" ? "iPhone" : "Android"}</div></>) },
          { key: "app", label: "App · gift", render: (r) => (<><div>{r.App_Name}</div><div className="wl-sub">{r.Gift_Label}</div></>) },
          { key: "agent", label: "Agent", render: (r) => (<><div>{r.Agent_Name}</div><div className="wl-sub cx-mono">{r.Agent_Code}</div></>) },
          { key: "status", label: "Status", render: (r) => <StatusBadge status={r.Status} /> },
          { key: "gift", label: "Gift", render: giftText },
          { key: "commission", label: "Commission", render: (r) => (r.Commission ? `${inr(r.Commission.Amount)} · ${r.Commission.Status}` : "—") },
          { key: "actions", label: "Actions", className: "wl-actions-col", render: actions },
        ]}
        mobileCard={(r) => ({
          title: r.Customer_Name,
          meta: [{ label: r.Status, tone: r.Status === "Registered" ? "warning" : r.Status === "Confirmed" ? "success" : "neutral" }],
          fields: [
            { label: "App", value: `${r.App_Name} · ${r.Platform === "ios" ? "iPhone" : "Android"}` },
            { label: "Mobile", value: phoneText(r.Mobile) },
            { label: "Agent", value: `${r.Agent_Name} · ${r.Agent_Code}` },
            { label: "Gift", value: giftText(r) },
            { label: "Commission", value: r.Commission ? `${inr(r.Commission.Amount)} · ${r.Commission.Status}` : "" },
          ],
          actions: actions(r),
        })}
      />
      {confirming ? (
        <ConfirmModal
          item={confirming}
          onClose={() => setConfirming(null)}
          onDone={() => {
            setNotice({ type: "success", text: `${confirming.Ref} confirmed.` });
            setConfirming(null);
            reload();
          }}
        />
      ) : null}
      {rejecting ? (
        <RejectModal
          item={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setNotice({ type: "success", text: `${rejecting.Ref} rejected.` });
            setRejecting(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}
